// src/features/system/developer/components/video-gallery/viewMode.ts
// ────────────────────────────────────────────────────────────────────────────
// Tiny persistence util for the Videos surface view mode.
//
// Why a dedicated module:
//   • DRY — both the panel and the (future) settings dropdown read/write
//     the same key. Centralising the key + parsing avoids drift.
//   • Resilient — guards SSR / private-mode / quota-exceeded localStorage
//     failures so a broken storage layer can never crash the dashboard.
//   • Typed — `VideoViewMode` is the single source of truth. Adding a
//     third mode (e.g. 'compact-list') is a one-line change here.
//
// UX rationale (YT/IG/TikTok parity):
//   YouTube, Instagram, and TikTok all remember the last-used layout
//   per-device and restore it on next visit. We do the same to honour the
//   user's chosen working mode without an extra click.
// ────────────────────────────────────────────────────────────────────────────

export type VideoViewMode = 'feed' | 'gallery';

const STORAGE_KEY = 'pac.videos.viewMode';
const VALID_MODES: ReadonlySet<VideoViewMode> = new Set<VideoViewMode>(['feed', 'gallery']);

/** Read the persisted view mode, falling back to 'feed' (TikTok-style default). */
export function readViewMode(): VideoViewMode {
  if (typeof window === 'undefined') return 'feed';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && VALID_MODES.has(raw as VideoViewMode)) return raw as VideoViewMode;
  } catch {
    // SSR / private mode / disabled storage — silently fall back.
  }
  return 'feed';
}

/** Persist the chosen view mode. Best-effort — never throws to callers. */
export function writeViewMode(mode: VideoViewMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Quota / disabled — ignore. In-memory state on the panel is still authoritative.
  }
}
